import moment from 'moment';
import { ConversionWirePeriod } from '@partner/feature-modules/dashboard/domain/model/conversion-metrics.model';
import {
  ApprovalRateInsight,
  ApprovalRateState,
  BestMonthInsight,
  DocumentsWaitingInsight,
  SmartInsight,
  SmartInsightRowVm,
  SmartInsightType,
  TeamSeatsInsight,
} from '@partner/feature-modules/dashboard/domain/model/smart-insights.model';
import { formatCompactCurrency } from './compact-currency.helper';
import { DELTA_LABEL_EDGES } from './conversion-metrics.helper';
import { CARD_LABEL_EDGES, percentLabel } from './pipeline-funnel.helper';

/** Material Icons Round names, from the design's icon component sets. */
export const SMART_INSIGHT_ICONS = {
  documentsWaiting: 'access_time',
  approvalRateUp: 'trending_up',
  approvalRateDown: 'trending_down',
  approvalRateFlat: 'trending_flat',
  approvalRateInsufficient: 'info',
  bestMonth: 'workspace_premium',
  teamSeats: 'warning',
} as const;

export const SMART_INSIGHT_CTA: Record<SmartInsightType, string> = {
  [SmartInsightType.DOCUMENTS_WAITING]: 'REVIEW',
  [SmartInsightType.APPROVAL_RATE]: 'SEE TREND',
  [SmartInsightType.BEST_MONTH]: 'SEE APPROVED',
  [SmartInsightType.TEAM_SEATS]: 'VIEW TEAM',
};

export interface SmartInsightCopy {
  headline: string;
  subline: string;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/** `Jan` from `2026-01`: read from the string, so no time zone can move it. */
function monthLabel(month: string): string {
  const parsed = moment(month, 'YYYY-MM', true);
  return parsed.isValid() ? parsed.format('MMM') : month;
}

export function documentsWaitingCopy(insight: DocumentsWaitingInsight): SmartInsightCopy {
  const applications = plural(insight.applications, 'application', 'applications');
  const days = plural(insight.thresholdDays, 'day', 'days');
  return {
    headline: `${applications} waiting on documents over ${days}`,
    subline: 'They stop aging the moment you upload.',
  };
}

/** The word the headline closes on, from the insight's own comparison window. */
export const APPROVAL_RATE_PERIOD_UNITS: Record<ConversionWirePeriod, string> = {
  [ConversionWirePeriod.MTD]: 'month',
  [ConversionWirePeriod.QTD]: 'quarter',
  [ConversionWirePeriod.YTD]: 'year',
  [ConversionWirePeriod.CUSTOM]: 'period',
};

/** Direction from the sign of the delta, never from the tone; a moved row without one reads as steady. */
export function approvalRateCopy(insight: ApprovalRateInsight): SmartInsightCopy {
  const unit = APPROVAL_RATE_PERIOD_UNITS[insight.comparison.period];
  if (insight.state === ApprovalRateState.INSUFFICIENT) {
    return {
      headline: `Not enough submission volume to compare this ${unit}`,
      subline: `Needs at least ${plural(insight.minSubmitted, 'submission', 'submissions')} in both periods.`,
    };
  }
  const change = percentLabel(Math.abs(insight.deltaPoints), DELTA_LABEL_EDGES);
  let headline = `Your approval rate hasn't materially changed this ${unit}`;
  if (insight.state === ApprovalRateState.MOVED && insight.deltaPoints > 0) {
    headline = `Your approval rate improved ${change} this ${unit}`;
  } else if (insight.state === ApprovalRateState.MOVED && insight.deltaPoints < 0) {
    headline = `Your approval rate declined ${change} this ${unit}`;
  }
  const current = percentLabel(insight.current.percent, CARD_LABEL_EDGES);
  const subline = insight.team
    ? `Now ${current} vs ${percentLabel(insight.team.percent, CARD_LABEL_EDGES)} team average.`
    : `Now ${current}, from ${percentLabel(insight.prior.percent, CARD_LABEL_EDGES)}.`;
  return { headline, subline };
}

function approvalRateIcon(insight: ApprovalRateInsight): string {
  if (insight.state === ApprovalRateState.INSUFFICIENT) {
    return SMART_INSIGHT_ICONS.approvalRateInsufficient;
  }
  if (insight.state === ApprovalRateState.MOVED && insight.deltaPoints > 0) {
    return SMART_INSIGHT_ICONS.approvalRateUp;
  }
  if (insight.state === ApprovalRateState.MOVED && insight.deltaPoints < 0) {
    return SMART_INSIGHT_ICONS.approvalRateDown;
  }
  return SMART_INSIGHT_ICONS.approvalRateFlat;
}

export function bestMonthCopy(insight: BestMonthInsight): SmartInsightCopy {
  const { monthToDateAmount, bestAmount } = insight;
  let headline: string;
  if (monthToDateAmount < bestAmount) {
    headline = `${formatCompactCurrency(bestAmount - monthToDateAmount)} away from your best month this year`;
  } else if (monthToDateAmount === bestAmount) {
    headline = "You've matched your best month this year";
  } else {
    headline = "You're ahead of your best month this year";
  }
  const subline =
    `${formatCompactCurrency(monthToDateAmount)} funded MTD · best month was ` +
    `${formatCompactCurrency(bestAmount)} in ${monthLabel(insight.bestMonth)}.`;
  return { headline, subline };
}

export function teamSeatsCopy(insight: TeamSeatsInsight): SmartInsightCopy {
  return {
    headline: `No submissions in ${plural(insight.daysIdle, 'day', 'days')} on ${plural(insight.seats, 'team seat', 'team seats')}`,
    subline: 'Accounts inactive for 90 days are at risk for closing.',
  };
}

function iconAndCopy(insight: SmartInsight): { icon: string; copy: SmartInsightCopy } {
  switch (insight.type) {
    case SmartInsightType.DOCUMENTS_WAITING:
      return { icon: SMART_INSIGHT_ICONS.documentsWaiting, copy: documentsWaitingCopy(insight.documentsWaiting) };
    case SmartInsightType.APPROVAL_RATE:
      return { icon: approvalRateIcon(insight.approvalRate), copy: approvalRateCopy(insight.approvalRate) };
    case SmartInsightType.BEST_MONTH:
      return { icon: SMART_INSIGHT_ICONS.bestMonth, copy: bestMonthCopy(insight.bestMonth) };
    case SmartInsightType.TEAM_SEATS:
      return { icon: SMART_INSIGHT_ICONS.teamSeats, copy: teamSeatsCopy(insight.teamSeats) };
  }
}

/** The tone travels as is: the row maps it to `data-tone`. */
export function toSmartInsightRowVm(insight: SmartInsight): SmartInsightRowVm {
  const { icon, copy } = iconAndCopy(insight);
  return {
    type: insight.type,
    tone: insight.tone,
    icon,
    headline: copy.headline,
    subline: copy.subline,
    ctaLabel: SMART_INSIGHT_CTA[insight.type],
    route: insight.link.route,
    queryParams: insight.link.queryParams,
  };
}
